/**
 * Identidad de sesión anónima del comprador (cabecera `x-seats-session`).
 *
 * Es un número aleatorio de 16 bytes (128 bits) en base64url, generado con `crypto.getRandomValues`, que identifica
 * al navegador (pestaña) frente al límite anti-acaparamiento del servidor. NO contiene ni deriva de ningún dato
 * personal: ni cuenta, ni correo, ni IP, ni huella del dispositivo. Se guarda en `sessionStorage`
 * (`seats-ai:sid`), así que sobrevive a recargas pero desaparece al cerrar la pestaña y no se comparte entre pestañas.
 *
 * El almacenamiento puede no existir o lanzar al tocarlo (modo privado, iframes con almacenamiento bloqueado): en ese
 * caso el id vive solo en memoria mientras dure la página, y nunca se rompe nada.
 */

export const SESSION_STORAGE_KEY = "seats-ai:sid";

const SID_RE = /^[A-Za-z0-9_-]{16,64}$/;
/** Caracteres imprimibles sin espacios: lo único que una cabecera HTTP admite sin que `fetch` lance. */
const TOKEN_RE = /^[\x21-\x7e]{1,512}$/;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

/** Entorno inyectable (las pruebas no tienen DOM). */
export interface SessionEnv {
  /** Devuelve el almacenamiento de sesión; puede lanzar o devolver null. */
  storage?: () => StorageLike | null | undefined;
  /** Devuelve `n` bytes aleatorios criptográficos, o null si el entorno no puede darlos. */
  random?: (n: number) => Uint8Array | null;
}

export const isSessionId = (v: unknown): v is string => typeof v === "string" && SID_RE.test(v);
export const isSessionToken = (v: unknown): v is string => typeof v === "string" && TOKEN_RE.test(v);

export function base64url(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** `sessionStorage`, o null si no existe. Tocarlo puede lanzar (iframes con almacenamiento bloqueado): llamar dentro de try. */
export const defaultStorage = (): StorageLike | null => (typeof sessionStorage === "undefined" ? null : sessionStorage);

const defaults: Required<SessionEnv> = {
  storage: defaultStorage,
  random: (n) => (typeof crypto === "undefined" || typeof crypto.getRandomValues !== "function" ? null : crypto.getRandomValues(new Uint8Array(n))),
};

/**
 * Crea un proveedor del id: lee el guardado (si es válido), si no genera uno nuevo y lo guarda; el resultado se
 * recuerda en memoria. Devuelve "" solo si el entorno no tiene `crypto.getRandomValues` (no se usa `Math.random`):
 * entonces no se envía la cabecera y el servidor aplica únicamente el límite por IP.
 */
export function sessionIdProvider(env: SessionEnv = {}): () => string {
  const storage = env.storage ?? defaults.storage;
  const random = env.random ?? defaults.random;
  let id = "";
  return () => {
    if (id) return id;
    try {
      const saved = storage()?.getItem(SESSION_STORAGE_KEY);
      if (isSessionId(saved)) id = saved;
    } catch {
      /* sin almacenamiento: se genera uno y se queda en memoria */
    }
    if (!id) {
      const bytes = random(16);
      if (!bytes) return "";
      id = base64url(bytes);
      try {
        storage()?.setItem(SESSION_STORAGE_KEY, id);
      } catch {
        /* no se pudo guardar: vale solo para esta página */
      }
    }
    return id;
  };
}

/** Proveedor por defecto: único por página, así que todas las instancias del widget comparten id. */
export const getSessionId: () => string = sessionIdProvider();
